import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Topbar from '../components/Topbar'
import { useToast } from '../context/ToastContext'
import { getCurrencySymbol } from '../utils/currencies'
import { useTranslation } from '../i18n/LanguageContext'

const STORAGE_KEY = 'pc_invoices_columns_v1'

// ── Column config — width drives grid-template-columns dynamically ─────────────
const ALL_COLUMNS = [
  { id:'sl',      label:'#',        width:'36px',  locked:true },
  { id:'invoice', label:'Invoice',  width:'130px' },
  { id:'client',  label:'Client',   width:'1fr',   locked:true },
  { id:'issue',   label:'Issue',    width:'105px' },
  { id:'due',     label:'Due',      width:'105px' },
  { id:'amount',  label:'Amount',   width:'105px', locked:true },
  { id:'status',  label:'Status',   width:'95px',  locked:true },
  { id:'actions', label:'Actions',  width:'150px', locked:true },
]
const DEFAULT_VISIBLE = Object.fromEntries(ALL_COLUMNS.map(c=>[c.id,true]))

const STATUS_CONFIG = {
  draft:     { bg:'var(--bg-muted)',      text:'var(--text-muted)', key:'status_draft' },
  sent:      { bg:'var(--primary-light)', text:'var(--primary)',    key:'status_sent' },
  paid:      { bg:'var(--success-light)', text:'var(--success)',    key:'status_paid' },
  overdue:   { bg:'var(--error-light)',   text:'var(--error)',      key:'status_overdue' },
  cancelled: { bg:'var(--error-light)',   text:'var(--error)',      key:'status_cancelled' },
  partial:   { bg:'var(--warning-light)', text:'var(--warning)',    key:'status_partial' },
}

const IC = {
  eye:      'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 12m-3 0a3 3 0 106 0 3 3 0 00-6 0',
  edit:     'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  trash:    'M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6',
  print:    'M6 9V2h12v7 M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2 M6 14h12v8H6z',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3',
  plus:     'M12 5v14M5 12h14',
  send:     'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  bell:     'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0',
  check:    'M20 6L9 17l-5-5',
  money:    'M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6',
  warn:     'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01',
  search:   'M21 21l-4.35-4.35 M11 19a8 8 0 100-16 8 8 0 000 16z',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  columns:  'M4 3a1 1 0 00-1 1v16a1 1 0 001 1h3V3H4zm5 0v18h2V3H9zm4 0v18h7a1 1 0 001-1V4a1 1 0 00-1-1h-7z',
  x:        'M6 18L18 6M6 6l12 12',
}

const FLAT = {
  cash:    'M10 18a8 8 0 100-16 8 8 0 000 16zM8.5 7.5a1.5 1.5 0 113 0c0 .56-.31 1.05-.77 1.31l-.73.43V10h-1V9l1.27-.75c.15-.09.23-.25.23-.42a.5.5 0 10-1 0H8.5zm1.5 4a.75.75 0 100 1.5.75.75 0 000-1.5z',
  check:   'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
  clock:   'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z',
}

function Icon({ path, size=14, color='var(--text-muted)', sw=1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path}/>
    </svg>
  )
}
function FlatIcon({ path, size=14, color='var(--text-muted)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color}>
      <path fillRule="evenodd" d={path} clipRule="evenodd"/>
    </svg>
  )
}

function ActionBtn({ onClick, title, iconPath, hoverBg, hoverColor, disabled }) {
  const [h, setH] = useState(false)
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      style={{ width:'28px', height:'28px', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'var(--radius-sm)', border:'none', cursor:disabled?'not-allowed':'pointer', background:h?hoverBg:'transparent', transition:'var(--transition-fast)', opacity:disabled?0.4:1 }}
      onMouseEnter={()=>!disabled&&setH(true)} onMouseLeave={()=>setH(false)}>
      <Icon path={iconPath} size={13} color={h?hoverColor:'var(--text-muted)'} sw={2}/>
    </button>
  )
}

// ── Animated counter ──────────────────────────────────────────────────────────
function useCountUp(target, duration=600) {
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (target===0) { setVal(0); return }
    const start = 0
    const t0 = performance.now()
    const tick = (now) => {
      const p = Math.min((now-t0)/duration, 1)
      const e = 1-Math.pow(1-p,3)
      setVal(Math.round(start+(target-start)*e))
      if (p<1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [target, duration])
  return val
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, iconPath, accentColor, loading, alert, isMoney, fmt }) {
  const counted = useCountUp(loading||isMoney?0:value)
  return (
    <div className="pc-card pc-card-hover" style={{ padding:'18px 20px', border:'1px solid '+(alert?'var(--error-border)':'var(--border)'), position:'relative', overflow:'hidden' }}>
      {alert&&<div style={{ position:'absolute', top:0, left:0, right:0, height:'3px', background:'var(--error)' }}/>}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'12px' }}>
        <span style={{ fontSize:'12px', fontWeight:500, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{label}</span>
        <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-md)', background:accentColor+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <FlatIcon path={iconPath} size={15} color={accentColor}/>
        </div>
      </div>
      <div style={{ fontSize:'24px', fontWeight:800, color:accentColor, letterSpacing:'-0.5px', lineHeight:1, fontFamily:'var(--font-ui)', marginBottom:'4px' }}>
        {loading
          ? <div className="pc-skeleton" style={{ width:'60%', height:'24px', borderRadius:'6px' }}/>
          : isMoney ? fmt(value) : counted}
      </div>
      <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{sub}</div>
    </div>
  )
}

const inp = { width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'8px 12px', fontSize:'12.5px', outline:'none', background:'var(--bg-card)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }

function Field({ label, value, onChange, placeholder, type='text', optional }) {
  return (
    <div>
      <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px', fontFamily:'var(--font-ui)' }}>
        {label}{optional&&<span style={{ fontWeight:400, color:'var(--text-muted)', marginLeft:'4px', opacity:0.7 }}>optional</span>}
      </label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{ ...inp }}
        onFocus={e=>{e.target.style.borderColor='var(--border-focus)';e.target.style.boxShadow='0 0 0 3px var(--primary-light)'}}
        onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='none'}}/>
    </div>
  )
}

function Card({ title, children, color='var(--primary)' }) {
  return (
    <div className="pc-card" style={{ overflow:'hidden', marginBottom:'14px' }}>
      <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)', background:'var(--bg-muted)' }}>
        <div style={{ fontSize:'11px', fontWeight:700, color, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:'var(--font-ui)' }}>{title}</div>
      </div>
      <div style={{ padding:'16px' }}>{children}</div>
    </div>
  )
}

function StatusPill({ status, t }) {
  const sc = STATUS_CONFIG[status]||STATUS_CONFIG.draft
  return (
    <span style={{ fontSize:'11px', fontWeight:600, padding:'3px 9px', borderRadius:'var(--radius-full)', background:sc.bg, color:sc.text, whiteSpace:'nowrap', fontFamily:'var(--font-ui)' }}>
      {t('invoices.'+sc.key)}
    </span>
  )
}

const BLANK_INVOICE = (settings) => ({
  invoice_number:'', client_name:'', client_email:'', client_phone:'', client_address:'',
  issue_date:new Date().toISOString().slice(0,10), due_date:'',
  notes:'', terms:settings?.invoice_terms||'', footer:settings?.invoice_footer_note||'',
  status:'draft',
  items:[{ description:'', quantity:1, unit_price:'', tax_percent:'', total:0 }],
  discount_percent:'', tax_type:settings?.tax_label||'VAT',
})

function calcItemTotal(item) {
  const base = Number(item.quantity||0)*Number(item.unit_price||0)
  return base + (item.tax_percent ? base*Number(item.tax_percent)/100 : 0)
}

function calcTotals(items, discountPercent) {
  const subtotal = items.reduce((s,i)=>s+Number(i.quantity||0)*Number(i.unit_price||0),0)
  const itemTax  = items.reduce((s,i)=>{ const base=Number(i.quantity||0)*Number(i.unit_price||0); return s+(i.tax_percent?base*Number(i.tax_percent)/100:0) },0)
  const discount = discountPercent ? Math.round(subtotal*Number(discountPercent)/100) : 0
  return { subtotal, itemTax, discount, total:subtotal+itemTax-discount }
}

function MiniBar({ data, height=60 }) {
  if (!data||data.length===0) return null
  const max = Math.max(...data.map(d=>d.value),1)
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:'4px', height:height+'px' }}>
      {data.map((d,i)=>(
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'3px' }}>
          <div style={{ width:'100%', borderRadius:'4px 4px 0 0', background:'var(--primary)', height:Math.max((d.value/max)*height, d.value>0?4:0)+'px', transition:'height 0.3s' }}/>
          <div style={{ fontSize:'9px', color:'var(--text-muted)', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{d.label}</div>
        </div>
      ))}
    </div>
  )
}

function DonutChart({ segments, size=80 }) {
  const total = segments.reduce((s,seg)=>s+seg.value,0)
  if (total===0) return <div style={{ width:size, height:size, borderRadius:'50%', background:'var(--bg-muted)' }}/>
  let offset = 0
  const r=30, cx=40, cy=40, circumference=2*Math.PI*r
  return (
    <svg width={size} height={size} viewBox="0 0 80 80">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-muted)" strokeWidth="12"/>
      {segments.filter(s=>s.value>0).map((seg,i)=>{
        const pct=seg.value/total; const dash=circumference*pct; const gap=circumference-dash
        const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color} strokeWidth="12" strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset*circumference/total*circumference/(2*Math.PI*r)-circumference*0.25} style={{ transform:`rotate(${-90+offset*360/total}deg)`, transformOrigin:'40px 40px' }}/>
        offset += seg.value; return el
      })}
      <text x={cx} y={cy+5} textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--text-primary)">{total}</text>
    </svg>
  )
}

// ── Filterable column header — Excel-style ──────────────────────────────────────
function FilterableHeader({ label, filterKey, openFilter, setOpenFilter, value, onChange, type='text', active }) {
  const isOpen = openFilter===filterKey
  return (
    <div style={{ position:'relative', display:'flex', alignItems:'center', gap:'4px' }}>
      <span style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{label}</span>
      <button onClick={(e)=>{ e.stopPropagation(); setOpenFilter(isOpen?null:filterKey) }}
        style={{ width:'16px', height:'16px', display:'flex', alignItems:'center', justifyContent:'center', border:'none', background:'none', cursor:'pointer', padding:0, flexShrink:0 }}>
        <svg width="10" height="10" viewBox="0 0 20 20" fill={active?'var(--primary)':'var(--text-muted)'}>
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.3 5.74V16a1 1 0 01-1.45.89l-2-1A1 1 0 018 15v-3.66L3.2 5.6A1 1 0 013 4z" clipRule="evenodd"/>
        </svg>
      </button>
      {isOpen&&(
        <div onClick={e=>e.stopPropagation()}
          style={{ position:'absolute', top:'22px', left:0, zIndex:50, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', padding:'10px', minWidth:'160px' }}>
          {type==='text'&&(
            <input type="text" autoFocus value={value} onChange={e=>onChange(e.target.value)} placeholder={`Filter ${label.toLowerCase()}...`}
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}
              onFocus={e=>e.target.style.borderColor='var(--border-focus)'}
              onBlur={e=>e.target.style.borderColor='var(--border)'}/>
          )}
          {type==='range'&&(
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              <input type="number" value={value.min} onChange={e=>onChange({...value,min:e.target.value})} placeholder="Min"
                style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}/>
              <input type="number" value={value.max} onChange={e=>onChange({...value,max:e.target.value})} placeholder="Max"
                style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}/>
            </div>
          )}
          {active&&(
            <button onClick={()=>{ onChange(type==='range'?{min:'',max:''}:''); setOpenFilter(null) }}
              style={{ marginTop:'8px', width:'100%', padding:'5px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'11px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Columns visibility dropdown ─────────────────────────────────────────────────
function ColumnsMenu({ visible, setVisible, open, setOpen }) {
  const hiddenCount = ALL_COLUMNS.filter(c=>!c.locked&&!visible[c.id]).length
  return (
    <div style={{ position:'relative' }}>
      <button onClick={(e)=>{ e.stopPropagation(); setOpen(o=>!o) }}
        style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'var(--radius-md)', border:'1px solid '+(hiddenCount>0?'var(--border-focus)':'var(--border)'), background:hiddenCount>0?'var(--primary-light)':'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:hiddenCount>0?'var(--primary)':'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
        <Icon path={IC.columns} size={13} color={hiddenCount>0?'var(--primary)':'var(--text-primary)'} sw={2}/>
        Columns
        {hiddenCount>0&&(
          <span style={{ fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'var(--radius-full)', background:'var(--primary)', color:'white' }}>{hiddenCount} hidden</span>
        )}
      </button>
      {open&&(
        <div onClick={e=>e.stopPropagation()}
          style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:60, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', padding:'10px', minWidth:'180px' }}>
          <div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px', fontFamily:'var(--font-ui)' }}>Show columns</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
            {ALL_COLUMNS.map(c=>(
              <label key={c.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'4px 6px', borderRadius:'var(--radius-sm)', cursor:c.locked?'default':'pointer', opacity:c.locked?0.5:1 }}>
                <input type="checkbox" checked={!!visible[c.id]} disabled={c.locked}
                  onChange={e=>setVisible(v=>({...v,[c.id]:e.target.checked}))}
                  style={{ accentColor:'var(--primary)', cursor:c.locked?'default':'pointer' }}/>
                <span style={{ fontSize:'12.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{c.label}{c.locked?' (always shown)':''}</span>
              </label>
            ))}
          </div>
          <button onClick={()=>setVisible(DEFAULT_VISIBLE)}
            style={{ marginTop:'8px', width:'100%', padding:'7px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
            Reset to default
          </button>
        </div>
      )}
    </div>
  )
}

function Invoices() {
  const { seller, isFeatureAllowed } = useAuth()
  const navigate   = useNavigate()
  const { showToast } = useToast()
  const { t }      = useTranslation()
  const currency   = seller?.seller_currency || 'BDT'
  const sym        = getCurrencySymbol(currency)
  const fmt        = useCallback((n) => sym+Math.abs(Math.round(n||0)).toLocaleString(), [sym])

  const [invoices, setInvoices]               = useState([])
  const [loading, setLoading]                 = useState(true)
  const [view, setView]                       = useState('list')
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [saving, setSaving]                   = useState(false)
  const [deleteId, setDeleteId]               = useState(null)
  const [deleting, setDeleting]               = useState(false)
  const [filterStatus, setFilterStatus]       = useState('all')
  const [settings, setSettings]               = useState(null)
  const [form, setForm]                       = useState(null)
  const [showPartialModal, setShowPartialModal] = useState(null)
  const [partialAmt, setPartialAmt]           = useState('')
  const [partialNote, setPartialNote]         = useState('')
  const [summary, setSummary]                 = useState({ total:0, paid:0, overdue:0, draft:0, partial:0, totalValue:0, paidValue:0, overdueValue:0, partialValue:0 })
  const [monthlyData, setMonthlyData]         = useState([])

  // ── New: search, sort, date filter, column filters, column visibility ──
  const [search, setSearch]                   = useState('')
  const [sortBy, setSortBy]                   = useState('newest')
  const [dateFrom, setDateFrom]               = useState('')
  const [dateTo, setDateTo]                   = useState('')
  const [showDateFilter, setShowDateFilter]   = useState(false)
  const [openFilter, setOpenFilter]           = useState(null)
  const [colFilters, setColFilters]           = useState({ client:'', amountMin:'', amountMax:'' })
  const [columnsOpen, setColumnsOpen]         = useState(false)
  const [visibleCols, setVisibleCols]         = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) return { ...DEFAULT_VISIBLE, ...JSON.parse(saved) }
    } catch {}
    return DEFAULT_VISIBLE
  })
  const [currentPage, setCurrentPage]         = useState(1)
  const PER_PAGE = 10

  const setF = (key, val) => setForm(f => ({ ...f, [key]:val }))

  useEffect(() => {
    if (seller?.id) { fetchInvoices(); fetchSettings() }
  }, [seller]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setCurrentPage(1) }, [search, filterStatus, dateFrom, dateTo, colFilters, sortBy])
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols)) } catch {}
  }, [visibleCols])
  useEffect(() => {
    if (!openFilter && !columnsOpen) return
    const handler = () => { setOpenFilter(null); setColumnsOpen(false) }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [openFilter, columnsOpen])

  const fetchSettings = async () => {
    const { data } = await supabase.from('seller_invoice_settings').select('*').eq('seller_id', seller.id).single()
    setSettings(data)
  }

  const fetchInvoices = async () => {
    setLoading(true)
    const { data } = await supabase.from('seller_invoices').select('*').eq('seller_id', seller.id).order('created_at', { ascending:false })
    const now = new Date()
    const processed = (data||[]).map(inv => {
      let status = inv.status
      if (status==='sent' && inv.due_date && new Date(inv.due_date)<now) status='overdue'
      return { ...inv, status }
    })
    setInvoices(processed)
    const paidInvs    = processed.filter(i=>i.status==='paid')
    const overdueInvs = processed.filter(i=>i.status==='overdue')
    const partialInvs = processed.filter(i=>i.status==='partial')
    setSummary({
      total:        processed.length,
      paid:         paidInvs.length,
      overdue:      overdueInvs.length,
      draft:        processed.filter(i=>i.status==='draft').length,
      partial:      partialInvs.length,
      totalValue:   processed.reduce((s,i)=>s+Number(i.total_amount||0),0),
      paidValue:    paidInvs.reduce((s,i)=>s+Number(i.total_amount||0),0),
      overdueValue: overdueInvs.reduce((s,i)=>s+Number(i.total_amount||0),0),
      partialValue: partialInvs.reduce((s,i)=>s+Number(i.total_amount||0)-Number(i.partial_payment_amount||0),0),
    })
    const months = []
    for (let m=5;m>=0;m--) {
      const d=new Date(); d.setMonth(d.getMonth()-m)
      const label = d.toLocaleString('default',{month:'short'})
      const value = paidInvs.filter(i=>i.paid_at&&new Date(i.paid_at).getMonth()===d.getMonth()&&new Date(i.paid_at).getFullYear()===d.getFullYear()).reduce((s,i)=>s+Number(i.total_amount||0),0)
      months.push({ label, value })
    }
    setMonthlyData(months)
    setLoading(false)
  }

  const generateInvoiceNumber = () => (settings?.invoice_prefix||'INV')+'-'+new Date().getFullYear()+'-'+Math.floor(1000+Math.random()*9000)

  const openCreate = (prefill=null) => {
    const blank = BLANK_INVOICE(settings)
    blank.invoice_number = generateInvoiceNumber()
    const dueDate = new Date(); dueDate.setDate(dueDate.getDate()+(settings?.default_due_days||7))
    blank.due_date = dueDate.toISOString().slice(0,10)
    setForm(prefill?{...blank,...prefill}:blank)
    setSelectedInvoice(null); setView('create')
  }

  const openEdit = (inv) => {
    setForm({ ...BLANK_INVOICE(settings), ...inv, items: Array.isArray(inv.items)?inv.items:[{ description:'', quantity:1, unit_price:'', tax_percent:'', total:0 }] })
    setSelectedInvoice(inv); setView('create')
  }

  const updateItem = (idx, key, val) => {
    setForm(f => {
      const items = f.items.map((item,i)=>{ if(i!==idx) return item; const updated={...item,[key]:val}; updated.total=calcItemTotal(updated); return updated })
      return { ...f, items }
    })
  }

  const handleSave = async (status='draft') => {
    if (!form.client_name || !form.invoice_number) { showToast(t('invoices.required_error'),'error'); return }
    setSaving(true)
    const { subtotal, itemTax, discount, total } = calcTotals(form.items, form.discount_percent)
    const payload = {
      seller_id:seller.id, invoice_number:form.invoice_number,
      client_name:form.client_name, client_email:form.client_email||null,
      client_phone:form.client_phone||null, client_address:form.client_address||null,
      issue_date:form.issue_date, due_date:form.due_date||null,
      notes:form.notes||null, terms:form.terms||null, footer:form.footer||null,
      status, items:form.items, subtotal, tax_amount:itemTax,
      discount_percent:form.discount_percent||null, discount_amount:discount||null,
      total_amount:total, tax_type:form.tax_type||'VAT', currency,
    }
    if (selectedInvoice?.id) {
      await supabase.from('seller_invoices').update(payload).eq('id',selectedInvoice.id)
      showToast(t('invoices.toast_updated'))
    } else {
      await supabase.from('seller_invoices').insert(payload)
      showToast(status==='sent'?t('invoices.toast_created_sent'):t('invoices.toast_saved_draft'))
    }
    await fetchInvoices(); setSaving(false); setView('list'); setSelectedInvoice(null); setForm(null)
  }

  const handleMarkPaid = async (inv) => {
    await supabase.from('seller_invoices').update({ status:'paid', paid_at:new Date().toISOString() }).eq('id',inv.id)
    await fetchInvoices()
    if (view==='detail') setSelectedInvoice(i=>({ ...i, status:'paid', paid_at:new Date().toISOString() }))
    showToast(t('invoices.toast_paid'))
  }

  const handlePartialPayment = async () => {
    if (!partialAmt) return
    await supabase.from('seller_invoices').update({ status:'partial', partial_payment_amount:Number(partialAmt), partial_payment_note:partialNote||null }).eq('id',showPartialModal.id)
    setShowPartialModal(null); setPartialAmt(''); setPartialNote('')
    await fetchInvoices(); showToast(t('invoices.toast_partial'))
  }

  const handleSendReminder = async (inv) => {
    const daysOverdue = inv.due_date ? Math.floor((new Date()-new Date(inv.due_date))/86400000) : 0
    const balance = Number(inv.total_amount||0)-Number(inv.partial_payment_amount||0)
    const msg = `Hi ${inv.client_name},\n\nThis is a friendly reminder regarding invoice ${inv.invoice_number} for ${sym}${balance.toLocaleString()}, which was due on ${inv.due_date}${daysOverdue>0?' ('+daysOverdue+' days ago)':''}.\n\nPlease arrange payment at your earliest convenience. Thank you.\n\n— ${seller?.shop_name||'PageConnect'}`
    await navigator.clipboard.writeText(msg)
    await supabase.from('seller_invoices').update({ last_reminder_sent:new Date().toISOString(), reminder_count:(inv.reminder_count||0)+1 }).eq('id',inv.id)
    await fetchInvoices()
    showToast(t('invoices.toast_reminder'))
  }

  const handleDelete = async () => {
    setDeleting(true)
    await supabase.from('seller_invoices').delete().eq('id',deleteId)
    setDeleteId(null); setDeleting(false)
    if (view==='detail') { setView('list'); setSelectedInvoice(null) }
    await fetchInvoices(); showToast(t('invoices.toast_deleted'))
  }

  const handleExportCSV = () => {
    const headers = ['Invoice #','Client','Email','Phone','Issue date','Due date','Amount','Tax','Discount','Total','Status','Paid at','Last reminder']
    const rows = filtered.map(i=>[i.invoice_number,i.client_name,i.client_email||'',i.client_phone||'',i.issue_date,i.due_date||'',i.subtotal||0,i.tax_amount||0,i.discount_amount||0,i.total_amount||0,i.status,i.paid_at||'',i.last_reminder_sent?new Date(i.last_reminder_sent).toLocaleDateString():''])
    const csv = [headers,...rows].map(r=>r.map(c=>'"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n')
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}))
    a.download = 'invoices-'+new Date().toISOString().slice(0,10)+'.csv'; a.click()
    showToast(t('invoices.toast_exported'))
  }

  function handlePrint(inv) { setSelectedInvoice(inv); setView('detail'); setTimeout(()=>window.print(),400) }

  const resetDateFilter = () => { setDateFrom(''); setDateTo(''); setShowDateFilter(false) }
  const hasDateFilter = dateFrom||dateTo
  const hasAmountFilter = colFilters.amountMin||colFilters.amountMax

  let filtered = invoices.filter(i => {
    const matchStatus = filterStatus==='all' ? true : i.status===filterStatus
    const q = search.toLowerCase()
    const matchSearch = !search || i.invoice_number?.toLowerCase().includes(q) || i.client_name?.toLowerCase().includes(q) || i.client_email?.toLowerCase().includes(q) || i.client_phone?.includes(search)
    let matchDate = true
    if (dateFrom) matchDate = matchDate && i.issue_date && new Date(i.issue_date)>=new Date(dateFrom)
    if (dateTo) { const to=new Date(dateTo); to.setHours(23,59,59); matchDate = matchDate && i.issue_date && new Date(i.issue_date)<=to }
    const matchClient = !colFilters.client || i.client_name?.toLowerCase().includes(colFilters.client.toLowerCase())
    const amt = Number(i.total_amount||0)
    const matchAmountMin = !colFilters.amountMin || amt>=Number(colFilters.amountMin)
    const matchAmountMax = !colFilters.amountMax || amt<=Number(colFilters.amountMax)
    return matchStatus&&matchSearch&&matchDate&&matchClient&&matchAmountMin&&matchAmountMax
  })

  filtered = [...filtered].sort((a,b)=>{
    switch(sortBy) {
      case 'oldest':      return new Date(a.issue_date||a.created_at)-new Date(b.issue_date||b.created_at)
      case 'amount_desc': return Number(b.total_amount||0)-Number(a.total_amount||0)
      case 'amount_asc':  return Number(a.total_amount||0)-Number(b.total_amount||0)
      case 'due_soon':    return new Date(a.due_date||'2999-01-01')-new Date(b.due_date||'2999-01-01')
      default:            return new Date(b.issue_date||b.created_at)-new Date(a.issue_date||a.created_at) // newest
    }
  })

  const totalPages = Math.max(1, Math.ceil(filtered.length/PER_PAGE))
  const pageStart  = (currentPage-1)*PER_PAGE
  const pageItems  = filtered.slice(pageStart, pageStart+PER_PAGE)

  const overdueList = invoices.filter(i=>i.status==='overdue').sort((a,b)=>new Date(a.due_date)-new Date(b.due_date))

  // Build dynamic grid template from visible columns
  const visibleColumns = ALL_COLUMNS.filter(c=>visibleCols[c.id])
  const TABLE_COLS = visibleColumns.map(c=>c.width).join(' ')
  const TABLE_GAP = 10
  const FR_MIN_PX = 220
  const TABLE_MIN_WIDTH = visibleColumns.reduce((sum,c)=>sum+(c.width.endsWith('fr')?FR_MIN_PX:parseInt(c.width)),0) + (visibleColumns.length-1)*TABLE_GAP + 32

  if (!isFeatureAllowed('invoices')) {
    return (
      <div className="pc-page" style={{ flex:1, background:'var(--bg-page)', fontFamily:'var(--font-body)' }}>
        <Topbar title={t('invoices.title')}/>
        <div style={{ padding:'48px 24px', maxWidth:'480px', margin:'0 auto', textAlign:'center' }}>
          <div className="pc-card" style={{ padding:'40px' }}>
            <div style={{ fontSize:'48px', marginBottom:'12px' }}>🧾</div>
            <div style={{ fontSize:'16px', fontWeight:700, color:'var(--text-primary)', marginBottom:'8px', fontFamily:'var(--font-ui)' }}>{t('invoices.upgrade_title')}</div>
            <div style={{ fontSize:'13px', color:'var(--text-muted)', marginBottom:'24px', lineHeight:1.6, fontFamily:'var(--font-body)' }}>{t('invoices.upgrade_desc')}</div>
            <button onClick={()=>navigate('/pricing')} style={{ width:'100%', padding:'13px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'14px', fontWeight:700, color:'white', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              {t('invoices.upgrade_btn')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── CREATE / EDIT VIEW ────────────────────────────────────────────────────
  if (view==='create' && form) {
    const { subtotal, itemTax, discount, total } = calcTotals(form.items, form.discount_percent)
    return (
      <div className="pc-page" style={{ flex:1, overflowX:'hidden', background:'var(--bg-page)', fontFamily:'var(--font-body)' }}>
        <Topbar title={selectedInvoice?t('invoices.edit_title'):t('invoices.create_title')}/>
        <div style={{ padding:'20px 24px', maxWidth:'860px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'18px' }}>
            <button onClick={()=>{ setView('list'); setForm(null); setSelectedInvoice(null) }}
              style={{ padding:'8px 16px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              {t('invoices.back')}
            </button>
            <h1 style={{ fontSize:'18px', fontWeight:800, color:'var(--text-primary)', fontFamily:'var(--font-ui)', margin:0 }}>{selectedInvoice?t('invoices.edit_title'):t('invoices.create_title')}</h1>
          </div>

          <Card title={t('invoices.invoice_details')}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }} className="iv-grid">
              <Field label={t('invoices.inv_number')+' *'} value={form.invoice_number} onChange={v=>setF('invoice_number',v)} placeholder={t('invoices.inv_number_ph')}/>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px', fontFamily:'var(--font-ui)' }}>{t('invoices.status_label')}</label>
                <select value={form.status} onChange={e=>setF('status',e.target.value)} style={{ ...inp, cursor:'pointer' }}
                  onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}>
                  <option value="draft">{t('invoices.status_draft')}</option>
                  <option value="sent">{t('invoices.status_sent')}</option>
                  <option value="paid">{t('invoices.status_paid')}</option>
                  <option value="cancelled">{t('invoices.status_cancelled')}</option>
                </select>
              </div>
              <Field label={t('invoices.issue_date')} value={form.issue_date} onChange={v=>setF('issue_date',v)} type="date"/>
              <Field label={t('invoices.due_date')} value={form.due_date} onChange={v=>setF('due_date',v)} type="date" optional/>
            </div>
          </Card>

          <Card title={t('invoices.client_details')}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }} className="iv-grid">
              <div style={{ gridColumn:'span 2' }} className="iv-span2">
                <Field label={t('invoices.client_name')+' *'} value={form.client_name} onChange={v=>setF('client_name',v)} placeholder={t('invoices.client_name_ph')}/>
              </div>
              <Field label={t('invoices.email_label')} value={form.client_email} onChange={v=>setF('client_email',v)} placeholder={t('invoices.email_ph')} type="email" optional/>
              <Field label={t('invoices.phone_label')} value={form.client_phone} onChange={v=>setF('client_phone',v)} placeholder={t('invoices.phone_ph')} optional/>
              <div style={{ gridColumn:'span 2' }} className="iv-span2">
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px', fontFamily:'var(--font-ui)' }}>{t('invoices.address_label')} <span style={{ fontWeight:400, color:'var(--text-muted)', opacity:0.7 }}>optional</span></label>
                <textarea value={form.client_address} onChange={e=>setF('client_address',e.target.value)} rows={2} placeholder={t('invoices.address_ph')} style={{ ...inp, resize:'none' }}
                  onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
              </div>
            </div>
          </Card>

          {/* Line items */}
          <div className="pc-card" style={{ overflow:'hidden', marginBottom:'14px' }}>
            <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)', background:'var(--bg-muted)' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'var(--primary)', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:'var(--font-ui)' }}>{t('invoices.line_items')}</div>
            </div>
            <div className="iv-item-header" style={{ display:'grid', gridTemplateColumns:'1fr 70px 110px 80px 100px 32px', gap:'8px', padding:'8px 16px', borderBottom:'1px solid var(--border)', background:'var(--bg-muted)' }}>
              {[t('invoices.col_desc'),t('invoices.col_qty'),t('invoices.col_unit'),t('invoices.col_tax_pct'),t('invoices.col_total'),''].map((h,i)=>(
                <div key={i} style={{ fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', fontFamily:'var(--font-ui)' }}>{h}</div>
              ))}
            </div>
            {form.items.map((item,idx)=>(
              <div key={idx} className="iv-item-row" style={{ display:'grid', gridTemplateColumns:'1fr 70px 110px 80px 100px 32px', gap:'8px', padding:'10px 16px', borderBottom:'1px solid var(--border)', alignItems:'center' }}>
                <input type="text" value={item.description} onChange={e=>updateItem(idx,'description',e.target.value)} placeholder={t('invoices.item_desc_ph')} style={{ ...inp, fontSize:'12px' }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                <input type="number" value={item.quantity} onChange={e=>updateItem(idx,'quantity',e.target.value)} min="1" placeholder="Qty" style={{ ...inp, fontSize:'12px', textAlign:'center' }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                <input type="number" value={item.unit_price} onChange={e=>updateItem(idx,'unit_price',e.target.value)} placeholder="Unit price" style={{ ...inp, fontSize:'12px', textAlign:'center' }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                <input type="number" value={item.tax_percent} onChange={e=>updateItem(idx,'tax_percent',e.target.value)} placeholder="Tax %" style={{ ...inp, fontSize:'12px', textAlign:'center' }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', textAlign:'right', fontFamily:'var(--font-body)' }}>
                  <span className="iv-mobile-label" style={{ fontSize:'10.5px', fontWeight:600, color:'var(--text-muted)', display:'none', marginRight:'4px', fontFamily:'var(--font-ui)' }}>{t('invoices.col_total')}:</span>
                  {sym}{calcItemTotal(item).toLocaleString()}
                </div>
                <button onClick={()=>setForm(f=>({ ...f, items:f.items.filter((_,i)=>i!==idx) }))}
                  style={{ width:'28px', height:'28px', borderRadius:'var(--radius-sm)', background:'var(--error-light)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--error)', fontSize:'16px', fontWeight:700 }}>×</button>
              </div>
            ))}
            <div style={{ padding:'12px 16px' }}>
              <button onClick={()=>setForm(f=>({ ...f, items:[...f.items,{ description:'', quantity:1, unit_price:'', tax_percent:'', total:0 }] }))}
                style={{ width:'100%', padding:'10px', borderRadius:'var(--radius-md)', border:'2px dashed var(--border)', background:'transparent', fontSize:'12.5px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                {t('invoices.add_line')}
              </button>
            </div>
          </div>

          <Card title={t('invoices.totals_title')}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'14px' }} className="iv-grid">
              <Field label={t('invoices.discount_pct')} value={form.discount_percent} onChange={v=>setF('discount_percent',v)} placeholder="0" type="number" optional/>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px', fontFamily:'var(--font-ui)' }}>{t('invoices.tax_label_field')}</label>
                <select value={form.tax_type} onChange={e=>setF('tax_type',e.target.value)} style={{ ...inp, cursor:'pointer' }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}>
                  <option>VAT</option><option>GST</option><option>Sales Tax</option><option>Other</option>
                </select>
              </div>
            </div>
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:'12px', display:'flex', flexDirection:'column', gap:'7px' }}>
              {[
                { label:t('invoices.subtotal'), value:subtotal },
                itemTax>0 && { label:form.tax_type, value:itemTax },
                discount>0 && { label:t('invoices.discount_pct')+' ('+form.discount_percent+'%)', value:-discount, red:true },
              ].filter(Boolean).map((row,i)=>(
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12.5px', fontFamily:'var(--font-body)' }}>
                  <span style={{ color:'var(--text-muted)' }}>{row.label}</span>
                  <span style={{ color:row.red?'var(--error)':'var(--text-primary)', fontWeight:500 }}>{row.red?'-':''}{sym}{Math.abs(row.value).toLocaleString()}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', borderTop:'1px solid var(--border)', paddingTop:'10px' }}>
                <span style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>{t('invoices.total')}</span>
                <span style={{ fontSize:'22px', fontWeight:800, color:'var(--primary)', fontFamily:'var(--font-ui)' }}>{sym}{total.toLocaleString()}</span>
              </div>
            </div>
          </Card>

          <Card title={t('invoices.notes_title')} color="var(--text-muted)">
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              {[[t('invoices.notes_label'),'notes',t('invoices.notes_ph')],[t('invoices.terms_label'),'terms',t('invoices.terms_ph')],[t('invoices.footer_label'),'footer',t('invoices.footer_ph')]].map(([label,key,ph])=>(
                <div key={key}>
                  <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px', fontFamily:'var(--font-ui)' }}>{label} <span style={{ fontWeight:400, color:'var(--text-muted)', opacity:0.7 }}>optional</span></label>
                  <textarea value={form[key]} onChange={e=>setF(key,e.target.value)} placeholder={ph} rows={2} style={{ ...inp, resize:'none' }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                </div>
              ))}
            </div>
          </Card>

          <div style={{ display:'flex', gap:'10px', marginBottom:'40px' }}>
            <button onClick={()=>handleSave('draft')} disabled={saving} style={{ flex:1, padding:'13px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', opacity:saving?0.6:1, fontFamily:'var(--font-ui)' }}>
              {saving?t('invoices.saving'):t('invoices.save_draft')}
            </button>
            <button onClick={()=>handleSave('sent')} disabled={saving} style={{ flex:2, padding:'13px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:700, color:'white', cursor:'pointer', opacity:saving?0.6:1, fontFamily:'var(--font-ui)' }}>
              {saving?t('invoices.saving'):t('invoices.save_sent')}
            </button>
          </div>
        </div>
        <style>{`
          @media(max-width:700px){
            .iv-grid { grid-template-columns:1fr!important }
            .iv-span2 { grid-column:span 1!important }
            .iv-item-header { display:none!important }
            .iv-item-row { display:flex!important; flex-wrap:wrap; gap:8px!important }
            .iv-item-row > *:nth-child(1) { flex:1 1 100% }
            .iv-item-row > *:nth-child(2) { flex:1 1 calc(33% - 6px) }
            .iv-item-row > *:nth-child(3) { flex:1 1 calc(33% - 6px) }
            .iv-item-row > *:nth-child(4) { flex:1 1 calc(33% - 6px) }
            .iv-item-row > *:nth-child(5) { flex:1 1 calc(100% - 40px); display:flex!important; align-items:center; justify-content:flex-start!important; text-align:left!important }
            .iv-item-row > *:nth-child(6) { flex:0 0 32px }
            .iv-mobile-label { display:inline!important }
          }
        `}</style>
      </div>
    )
  }

  // ── DETAIL VIEW ───────────────────────────────────────────────────────────
  if (view==='detail' && selectedInvoice) {
    const inv     = selectedInvoice
    const items   = Array.isArray(inv.items)?inv.items:[]
    const balance = Number(inv.total_amount||0)-Number(inv.partial_payment_amount||0)
    const daysOverdue = inv.due_date && inv.status==='overdue' ? Math.floor((new Date()-new Date(inv.due_date))/86400000) : 0

    return (
      <div className="pc-page" style={{ flex:1, overflowX:'hidden', background:'var(--bg-page)', fontFamily:'var(--font-body)' }}>
        <Topbar title={inv.invoice_number}/>
        <div style={{ padding:'20px 24px', maxWidth:'860px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'18px', flexWrap:'wrap' }} className="no-print">
            <button onClick={()=>{ setView('list'); setSelectedInvoice(null) }} style={{ padding:'8px 16px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              {t('invoices.back')}
            </button>
            <div style={{ flex:1 }}/>
            <button onClick={()=>window.print()} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              <Icon path={IC.print} color='var(--text-primary)' size={13} sw={2}/> {t('invoices.print_pdf')}
            </button>
            {inv.status!=='paid'&&inv.status!=='cancelled'&&(
              <>
                <button onClick={()=>handleMarkPaid(inv)} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'var(--radius-md)', background:'var(--success-light)', border:'1px solid var(--success-border)', fontSize:'12.5px', fontWeight:600, color:'var(--success)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                  <Icon path={IC.check} color='var(--success)' size={13} sw={2.5}/> {t('invoices.mark_paid')}
                </button>
                <button onClick={()=>{ setShowPartialModal(inv); setPartialAmt(''); setPartialNote('') }} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'var(--radius-md)', background:'var(--warning-light)', border:'1px solid var(--warning-border)', fontSize:'12.5px', fontWeight:600, color:'var(--warning)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                  <Icon path={IC.money} color='var(--warning)' size={13} sw={2}/> {t('invoices.partial_payment')}
                </button>
              </>
            )}
            {(inv.status==='overdue'||inv.status==='sent'||inv.status==='partial')&&(
              <button onClick={()=>handleSendReminder(inv)} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'var(--radius-md)', background:'var(--error-light)', border:'1px solid var(--error-border)', fontSize:'12.5px', fontWeight:600, color:'var(--error)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                <Icon path={IC.bell} color='var(--error)' size={13} sw={2}/> {t('invoices.send_reminder')}
              </button>
            )}
            <button onClick={()=>openEdit(inv)} style={{ padding:'8px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              {t('invoices.edit_btn')}
            </button>
          </div>

          {inv.status==='overdue'&&(
            <div style={{ padding:'12px 16px', borderRadius:'var(--radius-md)', background:'var(--error-light)', border:'1px solid var(--error-border)', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }} className="no-print">
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <Icon path={IC.warn} color='var(--error)' size={16} sw={2}/>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'var(--error)', fontFamily:'var(--font-ui)' }}>{t('invoices.overdue_title')} {daysOverdue} {daysOverdue!==1?t('invoices.overdue_days_label'):t('invoices.overdue_day')}</div>
                  <div style={{ fontSize:'11.5px', color:'var(--error)', fontFamily:'var(--font-body)' }}>{t('invoices.balance_due')} {sym}{balance.toLocaleString()}{inv.last_reminder_sent?' · '+t('invoices.last_reminded')+' '+new Date(inv.last_reminder_sent).toLocaleDateString():''}</div>
                </div>
              </div>
              <button onClick={()=>handleSendReminder(inv)} style={{ padding:'8px 14px', borderRadius:'var(--radius-md)', background:'var(--error)', border:'none', fontSize:'12.5px', fontWeight:600, color:'white', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                {t('invoices.copy_reminder')}
              </button>
            </div>
          )}

          {inv.status==='partial'&&(
            <div style={{ padding:'12px 16px', borderRadius:'var(--radius-md)', background:'var(--warning-light)', border:'1px solid var(--warning-border)', marginBottom:'14px' }} className="no-print">
              <div style={{ fontSize:'13px', fontWeight:700, color:'var(--warning)', fontFamily:'var(--font-ui)' }}>{t('invoices.partial_banner_title')} {sym}{Number(inv.partial_payment_amount||0).toLocaleString()} {t('invoices.partial_paid')}</div>
              <div style={{ fontSize:'11.5px', color:'var(--warning)', marginTop:'3px', fontFamily:'var(--font-body)' }}>{t('invoices.partial_balance')} {sym}{balance.toLocaleString()}{inv.partial_payment_note?' · '+inv.partial_payment_note:''}</div>
            </div>
          )}

          {/* Invoice document */}
          <div className="pc-card print-invoice" style={{ padding:'32px' }}>
            <div className="iv-doc-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'32px', gap:'16px' }}>
              <div>
                <div style={{ fontSize:'22px', fontWeight:800, color:'var(--text-primary)', marginBottom:'4px', fontFamily:'var(--font-ui)' }}>{seller?.shop_name}</div>
                {seller?.phone&&<div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{seller.phone}</div>}
                {seller?.district&&<div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{seller.district}</div>}
              </div>
              <div className="iv-doc-header-right" style={{ textAlign:'right' }}>
                <div style={{ fontSize:'28px', fontWeight:800, color:'var(--text-primary)', marginBottom:'6px', fontFamily:'var(--font-ui)' }}>INVOICE</div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'var(--primary)', fontFamily:'var(--font-mono)', marginBottom:'6px' }}>{inv.invoice_number}</div>
                <StatusPill status={inv.status} t={t}/>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'28px' }} className="iv-grid">
              <div>
                <div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'6px', fontFamily:'var(--font-ui)' }}>{t('invoices.detail_bill_to')}</div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>{inv.client_name}</div>
                {inv.client_email&&<div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{inv.client_email}</div>}
                {inv.client_phone&&<div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{inv.client_phone}</div>}
                {inv.client_address&&<div style={{ fontSize:'12px', color:'var(--text-muted)', marginTop:'4px', whiteSpace:'pre-line', fontFamily:'var(--font-body)' }}>{inv.client_address}</div>}
              </div>
              <div className="iv-dates" style={{ textAlign:'right' }}>
                {[[t('invoices.detail_issue'),inv.issue_date],[t('invoices.detail_due'),inv.due_date],[t('invoices.detail_currency'),currency]].filter(r=>r[1]).map(([l,v])=>(
                  <div key={l} style={{ display:'flex', justifyContent:'flex-end', gap:'16px', fontSize:'12.5px', marginBottom:'4px', fontFamily:'var(--font-body)' }}>
                    <span style={{ color:'var(--text-muted)' }}>{l}</span>
                    <span style={{ fontWeight:600, color:'var(--text-primary)' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Items — desktop table */}
            <div className="iv-items-table" style={{ borderRadius:'var(--radius-md)', border:'1px solid var(--border)', overflow:'hidden', marginBottom:'24px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 110px 70px 100px', gap:'8px', padding:'10px 16px', background:'var(--bg-muted)' }}>
                {[t('invoices.col_desc'),t('invoices.col_qty'),t('invoices.col_unit'),t('invoices.col_tax_pct'),t('invoices.col_total')].map(h=>(
                  <div key={h} style={{ fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', fontFamily:'var(--font-ui)' }}>{h}</div>
                ))}
              </div>
              {items.map((item,i)=>(
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 60px 110px 70px 100px', gap:'8px', padding:'10px 16px', borderTop:'1px solid var(--border)' }}>
                  <div style={{ fontSize:'12.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{item.description||'—'}</div>
                  <div style={{ fontSize:'12.5px', color:'var(--text-muted)', textAlign:'center', fontFamily:'var(--font-body)' }}>{item.quantity}</div>
                  <div style={{ fontSize:'12.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{sym}{Number(item.unit_price||0).toLocaleString()}</div>
                  <div style={{ fontSize:'12.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{item.tax_percent?item.tax_percent+'%':'—'}</div>
                  <div style={{ fontSize:'12.5px', fontWeight:700, color:'var(--text-primary)', textAlign:'right', fontFamily:'var(--font-body)' }}>{sym}{calcItemTotal(item).toLocaleString()}</div>
                </div>
              ))}
            </div>

            {/* Items — mobile cards */}
            <div className="iv-items-mobile" style={{ marginBottom:'24px' }}>
              {items.map((item,i)=>(
                <div key={i} style={{ borderRadius:'var(--radius-md)', border:'1px solid var(--border)', padding:'10px 14px', marginBottom:'8px' }}>
                  <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', marginBottom:'6px', fontFamily:'var(--font-body)' }}>{item.description||'—'}</div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                    <span>{item.quantity} × {sym}{Number(item.unit_price||0).toLocaleString()}{item.tax_percent?' (+'+item.tax_percent+'%)':''}</span>
                    <span style={{ fontWeight:700, color:'var(--text-primary)' }}>{sym}{calcItemTotal(item).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'24px' }}>
              <div style={{ width:'280px', maxWidth:'100%' }}>
                {[
                  { label:t('invoices.subtotal'), value:Number(inv.subtotal||0) },
                  Number(inv.tax_amount||0)>0 && { label:inv.tax_type||'VAT', value:Number(inv.tax_amount) },
                  Number(inv.discount_amount||0)>0 && { label:t('invoices.discount_pct')+' ('+inv.discount_percent+'%)', value:-Number(inv.discount_amount), red:true },
                ].filter(Boolean).map((row,i)=>(
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12.5px', marginBottom:'6px', fontFamily:'var(--font-body)' }}>
                    <span style={{ color:'var(--text-muted)' }}>{row.label}</span>
                    <span style={{ color:row.red?'var(--error)':'var(--text-primary)' }}>{row.red?'-':''}{sym}{Math.abs(row.value).toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', borderTop:'2px solid var(--border)', paddingTop:'10px', marginTop:'6px' }}>
                  <span style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>{t('invoices.total')}</span>
                  <span style={{ fontSize:'22px', fontWeight:800, color:'var(--primary)', fontFamily:'var(--font-ui)' }}>{sym}{Number(inv.total_amount||0).toLocaleString()}</span>
                </div>
                {inv.status==='partial'&&(
                  <>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12.5px', marginTop:'8px', fontFamily:'var(--font-body)' }}>
                      <span style={{ color:'var(--success)' }}>{t('invoices.status_paid')}</span>
                      <span style={{ color:'var(--success)', fontWeight:600 }}>{sym}{Number(inv.partial_payment_amount||0).toLocaleString()}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', fontWeight:700, marginTop:'4px', fontFamily:'var(--font-body)' }}>
                      <span style={{ color:'var(--warning)' }}>{t('invoices.partial_balance')}</span>
                      <span style={{ color:'var(--warning)' }}>{sym}{balance.toLocaleString()}</span>
                    </div>
                  </>
                )}
                {inv.status==='paid'&&(
                  <div style={{ marginTop:'8px', padding:'6px', borderRadius:'var(--radius-sm)', background:'var(--success-light)', textAlign:'center', fontSize:'12px', fontWeight:700, color:'var(--success)', fontFamily:'var(--font-ui)' }}>
                    ✓ {t('invoices.detail_paid_stamp')} {inv.paid_at?'· '+new Date(inv.paid_at).toLocaleDateString():''}
                  </div>
                )}
              </div>
            </div>

            {inv.notes&&<div style={{ marginBottom:'14px' }}><div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'4px', fontFamily:'var(--font-ui)' }}>{t('invoices.detail_notes')}</div><div style={{ fontSize:'12.5px', color:'var(--text-primary)', whiteSpace:'pre-line', fontFamily:'var(--font-body)' }}>{inv.notes}</div></div>}
            {inv.terms&&<div style={{ marginBottom:'14px' }}><div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'4px', fontFamily:'var(--font-ui)' }}>{t('invoices.detail_terms')}</div><div style={{ fontSize:'12.5px', color:'var(--text-primary)', whiteSpace:'pre-line', fontFamily:'var(--font-body)' }}>{inv.terms}</div></div>}
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:'16px', textAlign:'center' }}>
              {inv.footer&&<div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'6px', fontFamily:'var(--font-body)' }}>{inv.footer}</div>}
              <div style={{ fontSize:'11px', color:'var(--border)', fontFamily:'var(--font-body)' }}>Powered by PageConnect · pageconnect.io</div>
            </div>
          </div>
        </div>
        <style>{`
          @media print { .no-print{display:none!important} .print-invoice{border:none!important;box-shadow:none!important} body{background:white!important} }
          .iv-items-mobile { display:none }
          @media(max-width:700px){
            .iv-grid { grid-template-columns:1fr!important; gap:14px!important }
            .iv-dates { text-align:left!important }
            .iv-dates > div { justify-content:flex-start!important }
            .iv-doc-header { flex-direction:column }
            .iv-doc-header-right { text-align:left!important; width:100% }
            .iv-items-table { display:none!important }
            .iv-items-mobile { display:block!important }
          }
        `}</style>
      </div>
    )
  }

  // ── LIST VIEW ─────────────────────────────────────────────────────────────
  return (
    <div className="pc-page" style={{ flex:1, overflowX:'hidden', background:'var(--bg-page)', fontFamily:'var(--font-body)' }}>
      <Topbar title={t('invoices.title')}/>
      <div style={{ padding:'20px 24px' }}>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <h1 style={{ fontSize:'20px', fontWeight:800, color:'var(--text-primary)', fontFamily:'var(--font-ui)', letterSpacing:'-0.3px', margin:0 }}>{t('invoices.title')}</h1>
            <p style={{ fontSize:'13px', color:'var(--text-muted)', margin:'3px 0 0', fontFamily:'var(--font-body)' }}>{t('invoices.subtitle')}</p>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={handleExportCSV} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)', transition:'var(--transition-fast)' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-strong)';e.currentTarget.style.background='var(--bg-muted)'}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-card)'}}>
              <Icon path={IC.download} color='var(--text-primary)' size={13} sw={2}/> {t('invoices.export_btn')}
            </button>
            <button onClick={()=>openCreate()} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 18px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:600, color:'white', cursor:'pointer', fontFamily:'var(--font-ui)', boxShadow:'0 2px 8px rgba(14,165,233,0.3)', transition:'var(--transition-base)' }}
              onMouseEnter={e=>{e.currentTarget.style.background='var(--primary-hover)';e.currentTarget.style.boxShadow='0 4px 12px rgba(14,165,233,0.4)'}}
              onMouseLeave={e=>{e.currentTarget.style.background='var(--primary)';e.currentTarget.style.boxShadow='0 2px 8px rgba(14,165,233,0.3)'}}>
              <Icon path={IC.plus} color='white' size={13} sw={2.5}/> {t('invoices.new_invoice')}
            </button>
          </div>
        </div>

        {/* KPI stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }} className="iv-stat-grid">
          <StatCard label={t('invoices.kpi_total_value')} value={summary.totalValue} sub={summary.total+' '+t('invoices.kpi_invoices')} iconPath={FLAT.cash} accentColor='var(--text-primary)' loading={loading} isMoney fmt={fmt}/>
          <StatCard label={t('invoices.kpi_paid')}        value={summary.paidValue}  sub={summary.paid+' '+t('invoices.kpi_invoices')} iconPath={FLAT.check} accentColor='var(--success)' loading={loading} isMoney fmt={fmt}/>
          <StatCard label={t('invoices.kpi_overdue')}     value={summary.overdueValue} sub={summary.overdue+' '+t('invoices.kpi_overdue_sub')} iconPath={FLAT.warning} accentColor='var(--error)' loading={loading} isMoney fmt={fmt} alert={summary.overdue>0}/>
          <StatCard label={t('invoices.kpi_partial')}     value={summary.partialValue} sub={(summary.partial||0)+' '+t('invoices.kpi_invoices')} iconPath={FLAT.clock} accentColor='var(--warning)' loading={loading} isMoney fmt={fmt}/>
        </div>

        {/* Charts */}
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px', marginBottom:'16px' }} className="iv-chart-grid">
          <div className="pc-card" style={{ padding:'16px 20px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-ui)' }}>{t('invoices.chart_monthly_title')}</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'12px', fontFamily:'var(--font-body)' }}>{t('invoices.chart_monthly_sub')}</div>
            <MiniBar data={monthlyData} height={80}/>
          </div>
          <div className="pc-card" style={{ padding:'16px 20px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'12px', fontFamily:'var(--font-ui)' }}>{t('invoices.chart_status_title')}</div>
            <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
              <DonutChart size={80} segments={[{ value:summary.paid, color:'var(--success)' },{ value:summary.overdue, color:'var(--error)' },{ value:summary.draft, color:'var(--bg-muted)' },{ value:summary.partial||0, color:'var(--warning)' }]}/>
              <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                {[[t('invoices.status_paid'),summary.paid,'var(--success)'],[t('invoices.status_overdue'),summary.overdue,'var(--error)'],[t('invoices.status_draft'),summary.draft,'var(--text-muted)'],[t('invoices.status_partial'),summary.partial||0,'var(--warning)']].map(([l,v,c])=>(
                  <div key={l} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px' }}>
                    <div style={{ width:'7px', height:'7px', borderRadius:'2px', background:c, flexShrink:0 }}/>
                    <span style={{ color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{l}</span>
                    <span style={{ fontWeight:700, color:'var(--text-primary)', marginLeft:'auto', fontFamily:'var(--font-body)' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Overdue banner */}
        {overdueList.length>0&&(
          <div style={{ background:'var(--error-light)', borderRadius:'var(--radius-md)', border:'1px solid var(--error-border)', padding:'14px', marginBottom:'16px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
              <Icon path={IC.warn} color='var(--error)' size={14} sw={2}/>
              <span style={{ fontSize:'13px', fontWeight:700, color:'var(--error)', fontFamily:'var(--font-ui)' }}>
                {t('invoices.overdue_banner')} {overdueList.length} {overdueList.length!==1?t('invoices.clients_word'):t('invoices.client_word')} {t('invoices.owe')} {fmt(summary.overdueValue)}
              </span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {overdueList.map(inv=>{
                const daysOverdue = inv.due_date?Math.floor((new Date()-new Date(inv.due_date))/86400000):0
                const balance = Number(inv.total_amount||0)-Number(inv.partial_payment_amount||0)
                return (
                  <div key={inv.id} className="pc-card" style={{ padding:'10px 14px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>{inv.client_name}</div>
                      <div style={{ fontSize:'11.5px', color:'var(--error)', fontFamily:'var(--font-body)' }}>{inv.invoice_number} · {daysOverdue} {t('invoices.overdue_days')} · {t('invoices.balance_due')} {fmt(balance)}</div>
                      {inv.last_reminder_sent&&(
                        <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px', fontFamily:'var(--font-body)' }}>
                          {t('invoices.last_reminded')} {new Date(inv.last_reminder_sent).toLocaleDateString()} · {inv.reminder_count||0} {(inv.reminder_count||0)!==1?t('invoices.reminders_sent'):t('invoices.reminder_sent_one')}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                      <button onClick={()=>handleSendReminder(inv)} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'6px 12px', borderRadius:'var(--radius-sm)', background:'var(--error-light)', border:'1px solid var(--error-border)', fontSize:'12px', fontWeight:600, color:'var(--error)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                        <Icon path={IC.send} color='var(--error)' size={11} sw={2}/> {t('invoices.remind_btn')}
                      </button>
                      <button onClick={()=>{ setSelectedInvoice(inv); setView('detail') }} style={{ padding:'6px 12px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                        {t('invoices.view_btn')}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Status filter pills */}
        <div style={{ display:'flex', gap:'6px', marginBottom:'12px', overflowX:'auto', paddingBottom:'4px' }}>
          {[
            { key:'all',     label:t('invoices.filter_all'),     count:invoices.length },
            { key:'draft',   label:t('invoices.filter_draft'),   count:summary.draft },
            { key:'sent',    label:t('invoices.filter_sent'),    count:invoices.filter(i=>i.status==='sent').length },
            { key:'paid',    label:t('invoices.filter_paid'),    count:summary.paid },
            { key:'overdue', label:t('invoices.filter_overdue'), count:summary.overdue },
            { key:'partial', label:t('invoices.filter_partial'), count:summary.partial||0 },
          ].map(f=>{
            const isActive = filterStatus===f.key
            return (
              <button key={f.key} onClick={()=>setFilterStatus(f.key)}
                style={{ padding:'6px 14px', borderRadius:'var(--radius-full)', border:'1px solid', fontSize:'12px', fontWeight:600, cursor:'pointer', transition:'var(--transition-fast)', background:isActive?'var(--primary-light)':'var(--bg-card)', color:isActive?'var(--primary)':'var(--text-muted)', borderColor:isActive?'var(--primary)':'var(--border)', whiteSpace:'nowrap', flexShrink:0, fontFamily:'var(--font-ui)' }}>
                {f.label} <span style={{ opacity:0.7 }}>{f.count}</span>
              </button>
            )
          })}
        </div>

        {/* Search + sort + date + columns toolbar */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:'220px', position:'relative' }}>
            <input type="text" placeholder={t('invoices.search_ph')||'Search by invoice #, client, email, phone...'} value={search} onChange={e=>setSearch(e.target.value)}
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'9px 36px 9px 36px', fontSize:'12.5px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}
              onFocus={e=>{e.target.style.borderColor='var(--border-focus)';e.target.style.background='var(--bg-card)';e.target.style.boxShadow='0 0 0 3px var(--primary-light)'}}
              onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.background='var(--bg-muted)';e.target.style.boxShadow='none'}}/>
            <div style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)' }}>
              <Icon path={IC.search} color='var(--text-muted)' size={14}/>
            </div>
            {search&&(
              <button onClick={()=>setSearch('')} style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'14px' }}>×</button>
            )}
          </div>

          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'9px 12px', fontSize:'12.5px', outline:'none', background:'var(--bg-card)', color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-body)' }}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="amount_desc">Amount: High to Low</option>
            <option value="amount_asc">Amount: Low to High</option>
            <option value="due_soon">Due date: Soonest</option>
          </select>

          <button onClick={()=>setShowDateFilter(!showDateFilter)}
            style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'var(--radius-md)', border:'1px solid '+(hasDateFilter?'var(--border-focus)':'var(--border)'), background:hasDateFilter?'var(--primary-light)':'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:hasDateFilter?'var(--primary)':'var(--text-primary)', cursor:'pointer', flexShrink:0, fontFamily:'var(--font-ui)' }}>
            <Icon path={IC.calendar} size={13} color={hasDateFilter?'var(--primary)':'var(--text-primary)'} sw={2}/>
            {hasDateFilter?`${dateFrom||'...'} → ${dateTo||'...'}`:'Issue date'}
            {hasDateFilter&&<span onClick={e=>{e.stopPropagation();resetDateFilter()}} style={{ marginLeft:'2px', fontSize:'14px', color:'var(--primary)' }}>×</span>}
          </button>

          <ColumnsMenu visible={visibleCols} setVisible={setVisibleCols} open={columnsOpen} setOpen={setColumnsOpen}/>
        </div>

        {showDateFilter&&(
          <div className="pc-card" style={{ padding:'14px 16px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <label style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-ui)' }}>From</label>
              <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
                style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px', fontSize:'12.5px', outline:'none', color:'var(--text-primary)', fontFamily:'var(--font-body)', background:'var(--bg-card)', cursor:'pointer' }}/>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <label style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-ui)' }}>To</label>
              <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
                style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px', fontSize:'12.5px', outline:'none', color:'var(--text-primary)', fontFamily:'var(--font-body)', background:'var(--bg-card)', cursor:'pointer' }}/>
            </div>
            <button onClick={resetDateFilter}
              style={{ padding:'7px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              Reset
            </button>
            {hasDateFilter&&<div style={{ fontSize:'12px', color:'var(--primary)', fontWeight:600, fontFamily:'var(--font-body)' }}>{filtered.length} invoice{filtered.length!==1?'s':''} in range</div>}
          </div>
        )}

        {search&&<div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'10px', fontFamily:'var(--font-body)' }}>{filtered.length} {filtered.length!==1?'results':'result'} for "{search}"</div>}

        {/* Table */}
        {loading ? (
          <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
            <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid var(--primary)', borderTopColor:'transparent', animation:'spin 0.7s linear infinite', margin:'0 auto 12px' }}/>
            <div style={{ fontSize:'13px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{t('invoices.loading')}</div>
          </div>
        ) : filtered.length===0 ? (
          <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
            <div style={{ fontSize:'40px', marginBottom:'12px' }}>🧾</div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'6px', fontFamily:'var(--font-ui)' }}>{invoices.length===0?t('invoices.no_invoices'):t('invoices.no_match')}</div>
            <div style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'20px', fontFamily:'var(--font-body)' }}>{t('invoices.no_invoices_sub')}</div>
            {invoices.length===0&&(
              <button onClick={()=>openCreate()} style={{ padding:'10px 20px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:600, color:'white', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                {t('invoices.create_first')}
              </button>
            )}
          </div>
        ) : (
          <div className="pc-card" style={{ overflow:'hidden' }}>
            <div className="iv-table-scroll" style={{ overflowX:'auto', overflowY:'auto', maxHeight:'640px' }}>
              <div className="iv-table-wrap" style={{ minWidth:TABLE_MIN_WIDTH+'px' }}>
                <div className="iv-row" style={{ display:'grid', gridTemplateColumns:TABLE_COLS, gap:TABLE_GAP+'px', padding:'10px 18px', background:'var(--bg-muted)', borderBottom:'1px solid var(--border)', position:'sticky', top:0, zIndex:10 }}>
                  {visibleCols.sl&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('invoices.col_sl')}</div>}
                  {visibleCols.invoice&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('invoices.col_invoice')}</div>}
                  {visibleCols.client&&<FilterableHeader label={t('invoices.col_client')} filterKey="client" openFilter={openFilter} setOpenFilter={setOpenFilter} value={colFilters.client} onChange={v=>setColFilters(f=>({...f,client:v}))} active={!!colFilters.client}/>}
                  {visibleCols.issue&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('invoices.col_issue')}</div>}
                  {visibleCols.due&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('invoices.col_due')}</div>}
                  {visibleCols.amount&&<FilterableHeader label={t('invoices.col_amount')} filterKey="amount" openFilter={openFilter} setOpenFilter={setOpenFilter} type="range" value={{min:colFilters.amountMin,max:colFilters.amountMax}} onChange={v=>setColFilters(f=>({...f,amountMin:v.min,amountMax:v.max}))} active={!!hasAmountFilter}/>}
                  {visibleCols.status&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('invoices.col_status')}</div>}
                  {visibleCols.actions&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('invoices.col_actions')}</div>}
                </div>

                {pageItems.map((inv,idx)=>{
                  const balance     = Number(inv.total_amount||0)-Number(inv.partial_payment_amount||0)
                  const daysOverdue = inv.due_date&&inv.status==='overdue'?Math.floor((new Date()-new Date(inv.due_date))/86400000):0
                  return (
                    <div key={inv.id} className="iv-row" style={{ display:'grid', gridTemplateColumns:TABLE_COLS, gap:TABLE_GAP+'px', padding:'12px 18px', borderBottom:'1px solid var(--border)', alignItems:'center', cursor:'pointer', transition:'background 0.1s' }}
                      onClick={()=>{ setSelectedInvoice(inv); setView('detail') }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg-muted)'}
                      onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}>
                      {visibleCols.sl&&<div style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{pageStart+idx+1}</div>}
                      {visibleCols.invoice&&<div style={{ fontSize:'11.5px', fontWeight:700, color:'var(--primary)', fontFamily:'var(--font-mono)' }}>{inv.invoice_number}</div>}
                      {visibleCols.client&&(
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'2px', fontFamily:'var(--font-body)' }}>{inv.client_name}</div>
                          <div style={{ fontSize:'11.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{inv.client_email||inv.client_phone||'—'}</div>
                        </div>
                      )}
                      {visibleCols.issue&&<div style={{ fontSize:'11.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{inv.issue_date||'—'}</div>}
                      {visibleCols.due&&(
                        <div>
                          <div style={{ fontSize:'11.5px', color:inv.status==='overdue'?'var(--error)':'var(--text-primary)', fontFamily:'var(--font-body)' }}>{inv.due_date||'—'}</div>
                          {inv.status==='overdue'&&<div style={{ fontSize:'10.5px', color:'var(--error)', fontWeight:600, fontFamily:'var(--font-body)' }}>{daysOverdue}d {t('invoices.overdue_badge')}</div>}
                        </div>
                      )}
                      {visibleCols.amount&&(
                        <div>
                          <div style={{ fontSize:'12.5px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{fmt(inv.total_amount)}</div>
                          {inv.status==='partial'&&<div style={{ fontSize:'10.5px', color:'var(--warning)', fontFamily:'var(--font-body)' }}>{t('invoices.bal_label')} {fmt(balance)}</div>}
                          {inv.status==='paid'&&inv.paid_at&&<div style={{ fontSize:'10.5px', color:'var(--success)', fontFamily:'var(--font-body)' }}>{t('invoices.paid_label')} {new Date(inv.paid_at).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}</div>}
                        </div>
                      )}
                      {visibleCols.status&&<div><StatusPill status={inv.status} t={t}/></div>}
                      {visibleCols.actions&&(
                        <div style={{ display:'flex', alignItems:'center', gap:'2px' }} onClick={e=>e.stopPropagation()}>
                          <ActionBtn onClick={()=>{ setSelectedInvoice(inv); setView('detail') }} title={t('invoices.view_btn')} iconPath={IC.eye} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
                          <ActionBtn onClick={()=>openEdit(inv)} title={t('invoices.edit_btn')} iconPath={IC.edit} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
                          <ActionBtn onClick={()=>handlePrint(inv)} title={t('invoices.print_pdf')} iconPath={IC.print} hoverBg='#F5F3FF' hoverColor='#6D28D9'/>
                          {(inv.status==='overdue'||inv.status==='sent'||inv.status==='partial')&&(
                            <ActionBtn onClick={()=>handleSendReminder(inv)} title={t('invoices.send_reminder')} iconPath={IC.bell} hoverBg='var(--error-light)' hoverColor='var(--error)'/>
                          )}
                          <ActionBtn onClick={()=>setDeleteId(inv.id)} title={t('invoices.delete_confirm')} iconPath={IC.trash} hoverBg='var(--error-light)' hoverColor='var(--error)'/>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Mobile cards */}
            {pageItems.map((inv,idx)=>{
              const balance     = Number(inv.total_amount||0)-Number(inv.partial_payment_amount||0)
              const daysOverdue = inv.due_date&&inv.status==='overdue'?Math.floor((new Date()-new Date(inv.due_date))/86400000):0
              return (
                <div key={'m'+inv.id} className="iv-mobile-card" onClick={()=>{ setSelectedInvoice(inv); setView('detail') }}
                  style={{ display:'none', padding:'14px 16px', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px', gap:'8px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0 }}>
                      <span style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', flexShrink:0 }}>#{pageStart+idx+1}</span>
                      <span style={{ fontSize:'12px', fontWeight:700, color:'var(--primary)', fontFamily:'var(--font-mono)' }}>{inv.invoice_number}</span>
                    </div>
                    <StatusPill status={inv.status} t={t}/>
                  </div>
                  <div style={{ marginBottom:'10px' }}>
                    <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-body)' }}>{inv.client_name}</div>
                    <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{inv.client_email||inv.client_phone||'—'}</div>
                  </div>
                  <div style={{ display:'flex', gap:'12px', marginBottom:'10px', paddingTop:'8px', borderTop:'1px solid var(--border)' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Issue</div>
                      <div style={{ fontSize:'12px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{inv.issue_date||'—'}</div>
                    </div>
                    <div style={{ flex:1, textAlign:'right' }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Due</div>
                      <div style={{ fontSize:'12px', color:inv.status==='overdue'?'var(--error)':'var(--text-primary)', fontFamily:'var(--font-body)' }}>{inv.due_date||'—'}</div>
                      {inv.status==='overdue'&&<div style={{ fontSize:'10.5px', color:'var(--error)', fontWeight:600, fontFamily:'var(--font-body)' }}>{daysOverdue}d {t('invoices.overdue_badge')}</div>}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                    <div>
                      <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{fmt(inv.total_amount)}</div>
                      {inv.status==='partial'&&<div style={{ fontSize:'11px', color:'var(--warning)', fontFamily:'var(--font-body)' }}>{t('invoices.bal_label')} {fmt(balance)}</div>}
                      {inv.status==='paid'&&inv.paid_at&&<div style={{ fontSize:'11px', color:'var(--success)', fontFamily:'var(--font-body)' }}>{t('invoices.paid_label')} {new Date(inv.paid_at).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}</div>}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'6px', paddingTop:'10px', borderTop:'1px solid var(--border)' }} onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>{ setSelectedInvoice(inv); setView('detail') }}
                      style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.eye} size={14} color='var(--text-primary)' sw={2}/>
                    </button>
                    <button onClick={()=>openEdit(inv)}
                      style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.edit} size={14} color='var(--text-primary)' sw={2}/>
                    </button>
                    <button onClick={()=>handlePrint(inv)}
                      style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.print} size={14} color='#6D28D9' sw={2}/>
                    </button>
                    {(inv.status==='overdue'||inv.status==='sent'||inv.status==='partial')&&(
                      <button onClick={()=>handleSendReminder(inv)}
                        style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--error-border)', background:'var(--error-light)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Icon path={IC.bell} size={14} color='var(--error)' sw={2}/>
                      </button>
                    )}
                    <button onClick={()=>setDeleteId(inv.id)}
                      style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--error-border)', background:'var(--error-light)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.trash} size={14} color='var(--error)' sw={2}/>
                    </button>
                  </div>
                </div>
              )
            })}

            {/* Pagination */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 18px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', flexWrap:'wrap', gap:'8px' }}>
              <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                Showing <strong style={{ color:'var(--text-primary)' }}>{pageStart+1}–{Math.min(pageStart+PER_PAGE,filtered.length)}</strong> of <strong style={{ color:'var(--text-primary)' }}>{filtered.length}</strong>
              </div>
              {totalPages>1&&(
                <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                  <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1}
                    style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:currentPage===1?'not-allowed':'pointer', opacity:currentPage===1?0.4:1 }}>
                    ‹
                  </button>
                  {Array.from({length:Math.min(totalPages,7)},(_,i)=>{
                    let page
                    if(totalPages<=7) page=i+1
                    else if(currentPage<=4) page=i+1
                    else if(currentPage>=totalPages-3) page=totalPages-6+i
                    else page=currentPage-3+i
                    if(page<1||page>totalPages) return null
                    return (
                      <button key={page} onClick={()=>setCurrentPage(page)}
                        style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid '+(currentPage===page?'var(--primary)':'var(--border)'), background:currentPage===page?'var(--primary)':'var(--bg-card)', color:currentPage===page?'white':'var(--text-primary)', fontSize:'12px', fontWeight:currentPage===page?700:400, cursor:'pointer', fontFamily:'var(--font-body)' }}>
                        {page}
                      </button>
                    )
                  })}
                  <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages}
                    style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:currentPage===totalPages?'not-allowed':'pointer', opacity:currentPage===totalPages?0.4:1 }}>
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {deleteId&&(
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'16px' }}>
          <div className="animate-scale-in" style={{ background:'var(--bg-card)', borderRadius:'var(--radius-xl)', padding:'28px', maxWidth:'360px', width:'100%', fontFamily:'var(--font-ui)', textAlign:'center', border:'1px solid var(--border)', boxShadow:'var(--shadow-xl)' }}>
            <div style={{ width:'56px', height:'56px', borderRadius:'var(--radius-lg)', background:'var(--error-light)', border:'1px solid var(--error-border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              <Icon path={IC.trash} color='var(--error)' size={24} sw={1.5}/>
            </div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'6px' }}>{t('invoices.delete_title')}</div>
            <div style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'20px', fontFamily:'var(--font-body)' }}>{t('invoices.delete_desc')}</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={()=>setDeleteId(null)} style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>{t('invoices.delete_cancel')}</button>
              <button onClick={handleDelete} disabled={deleting} style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', background:'var(--error)', border:'none', fontSize:'13px', fontWeight:700, color:'white', cursor:'pointer', opacity:deleting?0.6:1, fontFamily:'var(--font-ui)' }}>
                {deleting?t('invoices.deleting'):t('invoices.delete_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partial payment modal */}
      {showPartialModal&&(
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'16px' }}>
          <div className="animate-scale-in" style={{ background:'var(--bg-card)', borderRadius:'var(--radius-xl)', padding:'24px', maxWidth:'380px', width:'100%', fontFamily:'var(--font-ui)', border:'1px solid var(--border)', boxShadow:'var(--shadow-xl)' }}>
            <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px' }}>{t('invoices.partial_modal_title')}</div>
            <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'16px', fontFamily:'var(--font-body)' }}>{t('invoices.partial_modal_total')} {fmt(showPartialModal.total_amount)}</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'12px', marginBottom:'16px' }}>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px', fontFamily:'var(--font-ui)' }}>{t('invoices.partial_amount')} ({sym})</label>
                <input type="number" value={partialAmt} onChange={e=>setPartialAmt(e.target.value)} placeholder="0" style={{ ...inp }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
              </div>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px', fontFamily:'var(--font-ui)' }}>{t('invoices.partial_note')} <span style={{ fontWeight:400, color:'var(--text-muted)', opacity:0.7 }}>optional</span></label>
                <input type="text" value={partialNote} onChange={e=>setPartialNote(e.target.value)} placeholder={t('invoices.partial_note_ph')} style={{ ...inp }} onFocus={e=>e.target.style.borderColor='var(--border-focus)'} onBlur={e=>e.target.style.borderColor='var(--border)'}/>
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={()=>setShowPartialModal(null)} style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>{t('invoices.partial_cancel')}</button>
              <button onClick={handlePartialPayment} style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:700, color:'white', cursor:'pointer', fontFamily:'var(--font-ui)' }}>{t('invoices.partial_save')}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform:rotate(360deg) } }
        @media(max-width:900px){
          .iv-stat-grid { grid-template-columns:1fr 1fr!important }
          .iv-chart-grid { grid-template-columns:1fr!important }
        }
        @media(max-width:768px){
          .iv-row { display:none!important }
          .iv-mobile-card { display:block!important }
          .iv-table-wrap { min-width:0!important }
          .iv-table-scroll { overflow-x:visible!important }
        }
        @media(max-width:540px){
          .iv-stat-grid { grid-template-columns:1fr 1fr!important }
        }
      `}</style>
    </div>
  )
}

export default Invoices